import { BoardGraph } from './graph';
import { findPath } from './pathfinder';

/**
 * Pre-computed distance cache for all node pairs in a board graph.
 * Uses BFS (via findPath) to compute shortest distances.
 *
 * Intended for boards of ~40-60 nodes (spec §8).
 * All-pairs BFS on a 60-node graph = 60 * 60 = 3600 BFS calls,
 * each on a ~60-node graph. This is well within budget for one-time computation.
 */
export class DistanceCache {
  /**
   * Sparse map: distanceMap.get(fromId)?.get(toId) === distance, or undefined if no path.
   * -1 sentinel is NOT stored; instead, missing keys mean no path.
   */
  private readonly distanceMap: Map<string, Map<string, number>>;

  /**
   * Build the cache from a BoardGraph.
   * Computes shortest distance from every node to every other node.
   */
  constructor(graph: BoardGraph) {
    this.distanceMap = new Map<string, Map<string, number>>();

    const allNodes = graph.getAllNodes();

    for (const sourceNode of allNodes) {
      const fromMap = new Map<string, number>();
      this.distanceMap.set(sourceNode.id, fromMap);

      for (const targetNode of allNodes) {
        const result = findPath(graph, sourceNode.id, targetNode.id);
        if (result.distance >= 0) {
          fromMap.set(targetNode.id, result.distance);
        }
        // If distance is -1 (no path), we don't store it — missing key means no path.
      }
    }
  }

  /**
   * Get the pre-computed shortest distance between two nodes.
   * Returns -1 if no path exists or if either node ID is unknown.
   */
  getDistance(fromId: string, toId: string): number {
    const fromMap = this.distanceMap.get(fromId);
    if (fromMap === undefined) return -1;
    const dist = fromMap.get(toId);
    return dist === undefined ? -1 : dist;
  }

  /**
   * Find the node from a set of candidate IDs that is closest to the target.
   * Useful for: "find nearest graveyard to X", "find nearest goal node to stalker".
   * Returns the closest candidate's ID, or null if no candidates have a path.
   * Ties broken by first match (stable).
   */
  findClosest(targetId: string, candidateIds: string[]): string | null {
    let bestId: string | null = null;
    let bestDist = Infinity;

    for (const candidateId of candidateIds) {
      const dist = this.getDistance(targetId, candidateId);
      if (dist >= 0 && dist < bestDist) {
        bestDist = dist;
        bestId = candidateId;
      }
    }

    return bestId;
  }

  /**
   * Find the node from a set of candidate IDs that is furthest from the target.
   * Useful for: "Game picks graveyard furthest from chest" (spec §3).
   * Returns the furthest candidate's ID, or null if no candidates have a path.
   * Ties broken by first match (stable).
   */
  findFurthest(targetId: string, candidateIds: string[]): string | null {
    let bestId: string | null = null;
    let bestDist = -1;

    for (const candidateId of candidateIds) {
      const dist = this.getDistance(targetId, candidateId);
      if (dist >= 0 && dist > bestDist) {
        bestDist = dist;
        bestId = candidateId;
      }
    }

    return bestId;
  }
}
